import { useDialogStore } from '@ariakit/react'
import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors
} from '@dnd-kit/core'
import {
	arrayMove,
	rectSortingStrategy,
	SortableContext
} from '@dnd-kit/sortable'
import { useEffect, useState } from 'react'
import { Chip } from '@/components/commons/chip'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { MoveIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { ExpVettingCriteriaGalleryUploadingItem } from '@/features/experience-vetting/components/exp-vetting-criteria-gallery-uploading-item'
import { useGalleryNeedsReviewShortcuts } from '@/features/photoset/hooks/use-gallery-needs-review-shortcuts'
import { useServicePhotoUpload } from '@/features/service-vetting/store/services-photo-upload'
import type {
	RemappedServiceProPhotos,
	ServiceGalleryImage,
	ServiceGalleryImageProPhotos
} from '@/features/service-vetting/types'
import { ProPhotosDeleteImageModal } from '@/features/submission-pro-photos-upload/components/pro-photos-delete-image-modal'
import { ProPhotosEmptyGalleryState } from '@/features/submission-pro-photos-upload/components/pro-photos-empty-gallery-state'
import { ProPhotosGalleryItem } from '@/features/submission-pro-photos-upload/components/pro-photos-gallery-item'
import { useTranslation } from '@/translations/i18n'
import { useDeleteServiceProPhotosImages } from '../query/use-delete-service-pro-photos-images'
import { useUpdateIsCoverGalleryImgProPhoto } from '../query/use-update-is-cover-gallery-img-pro-photo'
import { useUpdateServiceVettingGalleryImagesPositions } from '../query/use-update-service-vetting-gallery-images-positions'
import { ServiceVettingCriteriaGalleryUploadButtonProPhoto } from './service-vetting-criteria-gallery-upload-button-pro-photo'

export const MINIMUM_IMAGES_REQUIRED = 1

export const ServiceProPhotosGrid = ({
	service
}: {
	service: RemappedServiceProPhotos
}) => {
	const { t } = useTranslation()
	const { gallery } = service
	const [activeId, setActiveId] = useState<number | string | null>(null)
	const [orderedImages, setOrderedImages] = useState(gallery.images)
	const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(
		null
	)
	const [showGalleryModal, setShowGalleryModal] = useState(false)
	const uploadingItems = useServicePhotoUpload((store) => store.galleryItems)
	const deleteImageStore = useDialogStore()
	const [imageToDelete, setImageToDelete] =
		useState<ServiceGalleryImageProPhotos | null>(null)

	const updateServiceVettingGalleryImagesPositions =
		useUpdateServiceVettingGalleryImagesPositions({
			serviceId: service.id,
			galleryId: service.gallery.id
		})

	const updateServiceCoverPhotoMutation = useUpdateIsCoverGalleryImgProPhoto({
		galleryId: service.gallery.id,
		serviceId: service.id
	})

	const handleChangeCoverPhoto = (id: number) => {
		updateServiceCoverPhotoMutation.mutate({ isCover: true, imgId: id })
	}

	const onCloseDeleteModalClose = () => {
		setImageToDelete(null)
		deleteImageStore.toggle()
	}
	const deletePhotoMutation = useDeleteServiceProPhotosImages(
		{ galleryId: service.gallery.id, serviceId: service.id },
		{ onSuccess: onCloseDeleteModalClose }
	)
	const onClickDeletePhoto = (
		e: React.MouseEvent<HTMLButtonElement>,
		imageId: ServiceGalleryImage['id']
	) => {
		e.stopPropagation()
		deletePhotoMutation.mutate({ imageIds: [imageId] })
	}

	useEffect(() => {
		setOrderedImages(gallery.images)
	}, [gallery.images])

	const handleCloseModal = () => {
		setExpandedImageIndex(null)
		setShowGalleryModal(false)
	}

	const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

	const handleDragStart = (event: DragStartEvent) => {
		setActiveId(event.active.id)
	}
	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event

		if (!over) {
			setActiveId(null)
			return
		}

		const oldIndex = orderedImages.findIndex(
			(image) => image.id === active.id
		)
		const newIndex = orderedImages.findIndex(
			(image) => image.id === over.id
		)

		if (oldIndex !== newIndex) {
			// Update local state immediately for responsive UI
			const newOrderedImages = arrayMove(
				[...orderedImages],
				oldIndex,
				newIndex
			)
			setOrderedImages(newOrderedImages)

			updateServiceVettingGalleryImagesPositions.mutate(
				newOrderedImages.map((image, index) => ({
					id: image.id,
					position: index + 1
				}))
			)
		}

		setActiveId(null)
	}
	const handleDragCancel = () => {
		setActiveId(null)
	}

	const handleClickExpandedImage = (imageId: ServiceGalleryImage['id']) => {
		const index = orderedImages.findIndex((image) => image.id === imageId)
		setExpandedImageIndex(index)
		setShowGalleryModal(true)
	}

	useGalleryNeedsReviewShortcuts({
		selectableImages: orderedImages,
		currentDetailImageIndex: expandedImageIndex || 0,
		setCurrentDetailImageIndex: setExpandedImageIndex,
		onClose: handleCloseModal
	})

	return (
		<>
			{service.gallery.images.length > 0 ? (
				<div className='grid w-full grid-cols-[repeat(3,minmax(0,1fr))] lg:grid-cols-[repeat(2,minmax(0,1fr))]'>
					<DndContext
						collisionDetection={closestCenter}
						onDragCancel={handleDragCancel}
						onDragEnd={handleDragEnd}
						onDragStart={handleDragStart}
						sensors={sensors}
					>
						<SortableContext
							items={orderedImages}
							strategy={rectSortingStrategy}
						>
							{orderedImages.map((image) => {
								return (
									<ProPhotosGalleryItem
										bottomLeftSlot={
											image.isCover ? (
												<Chip
													label={t(
														'vetting:reordering.cover'
													)}
													shape='roundedRectangle'
													variant={'neutral'}
												/>
											) : (
												<Chip
													className='show-on-hover cursor-pointer [&_span]:underline [&_span]:decoration-[7.5%] [&_span]:decoration-neutral-400 [&_span]:decoration-solid [&_span]:underline-offset-auto'
													endSlot={
														updateServiceCoverPhotoMutation.isPending && (
															<Spinner />
														)
													}
													label={t(
														'vetting:reordering.set_as_cover'
													)}
													onClick={() =>
														!updateServiceCoverPhotoMutation.isPending &&
														handleChangeCoverPhoto(
															image.id
														)
													}
													shape='roundedRectangle'
													variant={'neutral'}
												/>
											)
										}
										imageId={image.id}
										imageName={image.name}
										imageUrl={
											image.thumbnails?.xs || image.url
										}
										isHostApprovalNeeded={
											image.isHostApprovalNeeded
										}
										isPendingImage={
											image.isPending ?? false
										}
										isProPhotography={
											image.isProPhotography ?? false
										}
										key={image.id}
										onClickExpand={() =>
											handleClickExpandedImage(image.id)
										}
										onDeleteModalOpen={() => {
											setImageToDelete(image)
											deleteImageStore.toggle()
										}}
									/>
								)
							})}
						</SortableContext>
						<DragOverlay style={{ transformOrigin: '0 0' }}>
							{activeId ? (
								<GalleryImageItem
									src={
										orderedImages.find(
											(image) => image.id === activeId
										)?.thumbnails?.xs ||
										orderedImages.find(
											(image) => image.id === activeId
										)?.url ||
										''
									}
									style={{
										cursor: 'grabbing'
									}}
									topLeftSlot={
										<ButtonLegacy
											className='show-on-hover'
											shape='square'
											size='sm'
											type='button'
											variant='secondary'
										>
											<MoveIcon size={16} />
										</ButtonLegacy>
									}
								/>
							) : null}
						</DragOverlay>
					</DndContext>

					{uploadingItems.map((image) => (
						<ExpVettingCriteriaGalleryUploadingItem
							hideAction={false}
							image={image}
							key={image.id}
						/>
					))}
				</div>
			) : (
				<ProPhotosEmptyGalleryState
					minimumImagesRequired={MINIMUM_IMAGES_REQUIRED}
					uploadButton={
						<ServiceVettingCriteriaGalleryUploadButtonProPhoto
							buttonProps={{
								variant: 'tertiary',
								size: 'sm',
								type: 'button'
							}}
							buttonText='vetting:reordering.upload_from_device'
							galleryId={service.gallery.id}
							serviceId={service.id}
							withIcon={false}
						/>
					}
				/>
			)}

			{showGalleryModal && expandedImageIndex !== null && (
				<GalleryModal
					currentImage={orderedImages[expandedImageIndex]}
					headerSlot={
						<GalleryModalHeader
							currentImage={orderedImages[expandedImageIndex]}
							imagesCount={orderedImages.length}
							onClose={handleCloseModal}
						/>
					}
					onClose={handleCloseModal}
					thumbnails={{
						l: orderedImages[expandedImageIndex].url || '',
						m: '',
						s: '',
						xs: ''
					}}
					thumbnailsSlot={
						<GalleryModalThumbnails
							currentImageId={
								orderedImages[expandedImageIndex].id
							}
							images={orderedImages.map((image) => ({
								id: image.id,
								name: image.name,
								thumbnails: {
									l: image.url || '',
									m: image.url || '',
									s: image.url || '',
									xs: image.url || ''
								}
							}))}
							onSelect={(_, index) => {
								setExpandedImageIndex(index)
							}}
							thumbnails='thumbnails'
						/>
					}
				/>
			)}

			{imageToDelete !== null && (
				<ProPhotosDeleteImageModal
					isPending={deletePhotoMutation.isPending}
					onClose={onCloseDeleteModalClose}
					onDelete={(e) => onClickDeletePhoto(e, imageToDelete.id)}
					store={deleteImageStore}
				/>
			)}
		</>
	)
}
